'use client';

import { faShare } from '@fortawesome/pro-light-svg-icons';
import { Laboratory, Partner, Professional } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { ReactNode } from 'react';

import { ShareButton } from '@/app/(main)/(pages)/(institucional)/_components/share-button.component';
import { entityActionStyle, entityActionsRowStyle } from '@/app/(main)/(pages)/_components/entities/entity-action.styles';
import { EntityFavoriteButton } from '@/app/(main)/(pages)/_components/entities/entity-favorite-button.component';
import { Icon } from '@/components/ui/icon.component';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';

interface EntityProfileActionsProps {
  entityType: EntityTypeEnum;
  entity: Professional | Partner | Laboratory;
  scheduleAction?: ReactNode;
  scheduleActionOnDesktop?: 'none' | 'always' | 'until-xl';
  shareLabel?: string;
}

const shareTrigger = (style: string, label?: string) => (openSheet: () => void) => (
  <button
    type="button"
    className={style}
    aria-label="Compartilhar perfil"
    data-testid={DataTestIdEnum.SHARED_ENTITY_CARD}
    onClick={openSheet}
  >
    <Icon icon={faShare} className="w-4 h-4 text-green-500" />
    {label}
  </button>
);

export function EntityProfileActions({
  entityType,
  entity,
  scheduleAction,
  scheduleActionOnDesktop = 'none',
  shareLabel = 'Compartilhar perfil',
}: EntityProfileActionsProps) {
  const favoriteEntityId = entity.id ? String(entity.id) : '';
  const { desktop, compact } = entityActionsRowStyle({ scheduleActionOnDesktop });

  const showsScheduleActionOnDesktop = scheduleActionOnDesktop !== 'none';
  const hidesScheduleActionOnLargeDesktop = scheduleActionOnDesktop === 'until-xl';

  const favoriteButton = (variant: 'icon' | 'labeled') => favoriteEntityId && (
    <EntityFavoriteButton
      entityId={favoriteEntityId}
      entityType={entityType}
      variant={variant}
    />
  );

  const shareButton = (style: string, label?: string) => (
    <ShareButton
      entityType={entityType}
      entity={entity}
      renderTrigger={shareTrigger(style, label)}
    />
  );

  return (
    <div>
      <div className={desktop()} data-testid={DataTestIdEnum.ENTITY_PROFILE_ACTIONS_DESKTOP}>
        {showsScheduleActionOnDesktop && (
          hidesScheduleActionOnLargeDesktop
            ? <div className="xl:hidden">{scheduleAction}</div>
            : scheduleAction
        )}

        {favoriteButton('labeled')}

        {shareButton(entityActionStyle({ variant: 'labeled', class: 'w-full' }), shareLabel)}
      </div>

      <div className={compact()} data-testid={DataTestIdEnum.ENTITY_PROFILE_ACTIONS_COMPACT}>
        {scheduleAction}

        {favoriteButton('icon')}

        <div className="shrink-0">
          {shareButton(entityActionStyle({ variant: 'icon' }))}
        </div>
      </div>
    </div>
  );
}
